import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { API } from '../config';
import './utilizzo.css';

const PERIODI = [{ g: 7, l: '7 giorni' }, { g: 30, l: '30 giorni' }, { g: 90, l: '90 giorni' }];
const TIPO_LABEL = { staff: 'Staff', link: 'Link', agente: 'Agente' };
const STRUM = [
  { k: 'n456', l: '456' }, { k: 'copertura', l: 'Cop.' }, { k: 'lucchetto', l: 'Lucch.' },
  { k: 'dominio', l: 'Dom.' }, { k: 'indaga', l: 'Indaga' }, { k: 'vodafone', l: 'VF' },
];

function fmtGiorni(g) {
  if (g == null) return '—';
  if (g === 0) return 'oggi';
  if (g === 1) return 'ieri';
  return `${g} gg fa`;
}

export default function UtilizzoPage() {
  const [giorni, setGiorni] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [tipo, setTipo] = useState('tutti');
  const [ord, setOrd] = useState('verifiche');   // 'verifiche' | 'ultima' | 'giorni_attivi'
  const [and, setAnd] = useState([]);
  const [sel, setSel] = useState(null);      // {tipo,id,nome}
  const [drill, setDrill] = useState(null);
  const drillReq = useRef(0);                // guardia anti-race su click rapidi
  const apri = (u) => {
    const my = ++drillReq.current;
    setSel({ tipo: u.tipo, id: u.id, nome: u.nome }); setDrill(null);
    axios.get(`${API}/utilizzo/utente/${u.tipo}/${encodeURIComponent(u.id)}?giorni=${giorni}`)
      .then(r => { if (my === drillReq.current) setDrill(r.data); })
      .catch(() => { if (my === drillReq.current) setDrill({ errore: true }); });
  };

  const carica = useCallback(() => {
    setLoading(true); setErr(false);
    axios.get(`${API}/utilizzo/riepilogo?giorni=${giorni}`)
      .then(r => setData(r.data))
      .catch(() => { setData(null); setErr(true); toast.error('Errore nel caricamento dell\'utilizzo'); })
      .finally(() => setLoading(false));
  }, [giorni]);
  useEffect(() => { carica(); }, [carica]);
  useEffect(() => {
    axios.get(`${API}/utilizzo/andamento?giorni=${giorni}`)
      .then(r => setAnd(r.data?.giorni || [])).catch(() => setAnd([]));
  }, [giorni]);

  const utenti = (data?.utenti || [])
    .filter(u => tipo === 'tutti' || u.tipo === tipo)
    .slice()
    .sort((a, b) => {
      if (ord === 'ultima') return (b.ultima || '').localeCompare(a.ultima || '');
      if (ord === 'giorni_attivi') return b.giorni_attivi - a.giorni_attivi;
      return b.verifiche - a.verifiche;
    });
  const tot = data?.totali || {};

  return (
    <div className="utl">
      <div className="utl-head">
        <h1>Utilizzo</h1>
        <div className="utl-periodi">
          {PERIODI.map(p => (
            <button key={p.g} className={`utl-pill${giorni === p.g ? ' on' : ''}`} onClick={() => setGiorni(p.g)}>{p.l}</button>
          ))}
        </div>
      </div>

      <div className="utl-kpis">
        <div className="utl-kpi"><div className="utl-kpi-v">{tot.utenti_attivi ?? '—'}</div><div className="utl-kpi-l">Utenti attivi</div></div>
        <div className="utl-kpi"><div className="utl-kpi-v">{(tot.verifiche ?? 0).toLocaleString('it-IT')}</div><div className="utl-kpi-l">Verifiche nel periodo</div></div>
        <div className="utl-kpi"><div className="utl-kpi-v">{tot.inattivi ?? '—'}</div><div className="utl-kpi-l">Inattivi (&ge; 14 gg)</div></div>
        <div className="utl-kpi"><div className="utl-kpi-v">{tot.senza_attivita ?? '—'}</div><div className="utl-kpi-l">Senza attività</div></div>
      </div>

      <div className="utl-chart">
        <div className="utl-chart-h">Andamento verifiche</div>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={and} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #e2e8f0)" vertical={false} />
            <XAxis dataKey="data" tickFormatter={d => d.slice(8) + '/' + d.slice(5, 7)} tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} interval="preserveStartEnd" />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-secondary, #64748b)' }} width={34} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} labelFormatter={d => `Giorno ${d}`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="staff" name="Staff" stackId="a" fill="#14746f" radius={[0, 0, 0, 0]} />
            <Bar dataKey="link" name="Link" stackId="a" fill="#3283bd" />
            <Bar dataKey="agente" name="Agente" stackId="a" fill="#e0662e" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="utl-toolbar">
        <div className="utl-seg">
          {['tutti', 'staff', 'link', 'agente'].map(t => (
            <button key={t} className={tipo === t ? 'on' : ''} onClick={() => setTipo(t)}>{t === 'tutti' ? 'Tutti' : TIPO_LABEL[t]}</button>
          ))}
        </div>
        <div className="utl-ord">
          Ordina:
          <select value={ord} onChange={e => setOrd(e.target.value)}>
            <option value="verifiche">Più attivi</option>
            <option value="giorni_attivi">Giorni attivi</option>
            <option value="ultima">Ultima attività</option>
          </select>
        </div>
      </div>

      <div className="utl-tablewrap">
        {loading ? <div className="utl-empty">Caricamento…</div> : (
          <table className="utl-table">
            <thead>
              <tr>
                <th className="l">Utente</th><th>Tipo</th><th>Verifiche</th><th>Oggi</th>
                <th>Giorni attivi</th><th>Accessi</th><th>Ultima attività</th><th className="l">Per strumento</th>
              </tr>
            </thead>
            <tbody>
              {utenti.map(u => (
                <tr key={`${u.tipo}-${u.id}`} className={u.inattivo ? 'inatt' : ''} onClick={() => apri(u)} style={{ cursor: 'pointer' }}>
                  <td className="l"><span className="utl-name">{u.nome}</span>{!u.attivo && <span className="utl-off">off</span>}</td>
                  <td><span className={`utl-badge tb-${u.tipo}`}>{TIPO_LABEL[u.tipo] || u.tipo}</span></td>
                  <td className="num strong">{u.verifiche.toLocaleString('it-IT')}</td>
                  <td className="num">{u.oggi || '—'}</td>
                  <td className="num">{u.giorni_attivi || '—'}</td>
                  <td className="num">{u.accessi || '—'}</td>
                  <td className={u.inattivo ? 'inatt-c' : ''}>{fmtGiorni(u.inattivo_da_giorni)}</td>
                  <td className="l"><span className="utl-strum">{STRUM.filter(s => u.per_strumento[s.k]).map(s => (
                    <span key={s.k} className="utl-chip">{s.l} {u.per_strumento[s.k]}</span>
                  ))}</span></td>
                </tr>
              ))}
              {utenti.length === 0 && <tr><td colSpan={8} className="utl-empty">{err ? 'Errore nel caricamento. Riprova.' : 'Nessun utente nel periodo.'}</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {sel && (
        <div className="utl-scrim" onClick={() => setSel(null)}>
          <div className="utl-drill" onClick={e => e.stopPropagation()}>
            <div className="utl-drill-h">
              <div><span className={`utl-badge tb-${sel.tipo}`}>{TIPO_LABEL[sel.tipo] || sel.tipo}</span> <b>{sel.nome}</b></div>
              <button className="utl-x" onClick={() => setSel(null)}>×</button>
            </div>
            {!drill ? <div className="utl-empty">Caricamento…</div> : drill.errore ? <div className="utl-empty">Errore nel caricamento.</div> : (
              <div className="utl-drill-body">
                <ResponsiveContainer width="100%" height={170}>
                  <BarChart data={drill.serie} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-card, #e2e8f0)" vertical={false} />
                    <XAxis dataKey="data" tickFormatter={d => d.slice(8) + '/' + d.slice(5, 7)} tick={{ fontSize: 9, fill: 'var(--text-secondary, #64748b)' }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: 'var(--text-secondary, #64748b)' }} width={30} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
                    <Bar dataKey="n" name="Verifiche" fill="#14746f" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <div className="utl-drill-strum">
                  {STRUM.map(s => (
                    <div key={s.k} className="utl-ds"><div className="utl-ds-v">{drill.per_strumento?.[s.k] || 0}</div><div className="utl-ds-l">{s.l}</div></div>
                  ))}
                </div>
                <div className="utl-drill-cols">
                  <div>
                    <div className="utl-drill-t">Ultimi accessi</div>
                    {(drill.accessi || []).length === 0 ? <div className="utl-empty sm">Nessun accesso registrato.</div> : (
                      <ul className="utl-list">
                        {drill.accessi.map((a, i) => (
                          <li key={i}><span className={a.esito === 'ok' ? 'ok' : 'ko'}>{a.esito === 'ok' ? '✓' : '✕'}</span> {(a.ts || '').slice(0, 16).replace('T', ' ')} · {a.device || a.ip || '—'}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="utl-drill-t">Ultime verifiche</div>
                    {(drill.ultime || []).length === 0 ? <div className="utl-empty sm">Nessuna verifica.</div> : (
                      <ul className="utl-list">
                        {drill.ultime.map((u2, i) => (
                          <li key={i}><b>{u2.tipo}</b> {(u2.at || '').slice(0, 16).replace('T', ' ')} · {u2.esito || '—'}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
